import { useMemo } from "react";
import { Share2, Sparkles } from "lucide-react";
import {
  useAdaptiveFanProfile,
  PERSONALITY_META,
  DNA_META,
} from "@/lib/adaptiveFan/adaptiveFanProfile";
import { trackEvent } from "@/lib/audit";
import { openShareSheet } from "@/components/viral/ShareSheetHost";

/**
 * Fan DNA — small premium badge that reflects the adaptive fan profile.
 * Local-first, no network. Compact, sharable feel, low jerarquía visual.
 */
export function FanDnaCard({
  className = "",
  shareable = false,
}: {
  className?: string;
  shareable?: boolean;
}) {
  const profile = useAdaptiveFanProfile();
  const dna = DNA_META[profile.dna];
  const persona = PERSONALITY_META[profile.personality];

  const totalSignals = useMemo(
    () =>
      Object.values(profile.counters).reduce<number>(
        (acc, n) => acc + (typeof n === "number" ? n : 0),
        0,
      ),
    [profile.counters],
  );

  const onShare = async () => {
    void trackEvent("fan_dna_shared", { dna: profile.dna, personality: profile.personality });
    openShareSheet();
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-border/60 bg-card/80 p-3 backdrop-blur-md ${className}`}
      style={{ boxShadow: `0 0 0 1px color-mix(in oklch, ${dna.color} 18%, transparent)` }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-2xl"
          style={{ background: `color-mix(in oklch, ${dna.color} 18%, transparent)` }}
          aria-hidden
        >
          {dna.emoji}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-muted-foreground">
            <Sparkles className="h-3 w-3" /> Fan DNA
          </div>
          <div className="truncate text-sm font-black" style={{ color: dna.color }}>
            {profile.dna}
          </div>
          <div className="truncate text-[11px] text-muted-foreground">{dna.tagline}</div>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span aria-hidden>{persona.emoji}</span>
          <span className="font-semibold">{persona.label}</span>
        </span>
        <span className="flex items-center gap-2">
          <span className="font-mono tabular-nums">
            {totalSignals} · {Math.round(profile.confidence * 100)}%
          </span>
          {shareable && (
            <button
              type="button"
              onClick={onShare}
              aria-label="Compartir Fan DNA"
              className="flex h-6 w-6 items-center justify-center rounded-full border text-foreground/80 transition hover:text-foreground"
              style={{
                borderColor: `color-mix(in oklch, ${dna.color} 40%, transparent)`,
                background: `color-mix(in oklch, ${dna.color} 12%, transparent)`,
              }}
            >
              <Share2 className="h-3 w-3" />
            </button>
          )}
        </span>
      </div>
    </div>
  );
}
